// Libs
import React, { ReactElement } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import clsx from 'clsx';
import { Button, IconButton, Icon, ClickAwayListener, Stack, Avatar, Typography, Divider } from 'ui-components';
import { useRouter } from 'next/router';
// App
import sharedStyles from 'app-shared/lib/layout/shared/header/main-nav.module.scss';
import navButtonStyles from 'app-shared/lib/layout/shared/header/nav-button.module.scss';
import { AUTHORITY } from 'app-shared/lib/api/user-access/account';
import { useGetAccount } from 'app-shared/lib/api/user-access/account';
import { hasAuthority } from 'app-shared/lib/api/user-access/auth/auth.utils';
import Brand from 'app-shared/lib/layout/shared/header/brand';
import useNavContentVisibility from 'app-shared/lib/layout/shared/header/nav-content-visibility.hooks';
import { SHOP_ADMIN_PAGES_URLS } from '@/fragments/shop-administration/shop-administration.constants';
import { isRouterPathMatchingRoute } from 'app-shared/lib/utils/router.utils';
import { useGetShop } from 'app-shared/lib/api/shop-administration/shop';
import { formatAvatarInitials, formatUserName } from 'app-shared/lib/utils';
import { UnstyledLink } from '@/components/navigation';
import StatsSvg from '../_shared/icons/stats.svg';
import BarsSortSvg from '../_shared/icons/bars-sort.svg';
import FileInvoiceDollarSvg from '../_shared/icons/file-invoice-dollar.svg';
import TargetAudienceSvg from '../_shared/icons/target-audience.svg';
import BrowserSvg from '../_shared/icons/browser.svg';
import SettingsSliderSvg from '../_shared/icons/settings.svg';
import { SHOP_ADMIN_ACCOUNT_PAGES_URLS } from '@/fragments/shop-administration/account/account.constants';

const MainNav = (): ReactElement => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const isSessionLoading = status == 'loading';
  const isUserShopAdmin = session && hasAuthority(session, AUTHORITY.SHOP_ADMIN);
  const isUserOrderAdmin = session && hasAuthority(session, AUTHORITY.SHOP_ORDER_ADMIN);
  const { data: account, isValidating: isAccountLoading } = useGetAccount(isUserShopAdmin);
  const shopId = account && account.shopId;
  const { data: shop, isValidating: isShopLoading } = useGetShop(shopId);
  const { navContentVisible, toggleNavContentVisibility, handleClickAwayNavContent } = useNavContentVisibility();
  const userName = formatUserName(account?.firstName, account?.lastName);

  if (isSessionLoading) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAwayNavContent}>
      <nav className={sharedStyles.nav}>
        <div className={sharedStyles.header}>
          <IconButton className={sharedStyles.button} onClick={toggleNavContentVisibility} size="large">
            <Icon name="menu" color="tertiary" size="extra-large" />
          </IconButton>
          <Brand className={sharedStyles.brand} />
        </div>
        <div className={clsx(sharedStyles.content, navContentVisible && sharedStyles.visible)}>
          {(!isAccountLoading && (
            <>
              {isUserShopAdmin && shopId && (
                <>
                  <Link href={`${SHOP_ADMIN_PAGES_URLS['shop-details']}?shp_id=${shopId}`}>
                    <Button
                      variant="text"
                      color="tertiary"
                      startIcon={<SettingsSliderSvg className={sharedStyles.icon} />}
                      className={clsx(
                        navButtonStyles.navButton,
                        isRouterPathMatchingRoute(router.pathname, `^${SHOP_ADMIN_PAGES_URLS['shop-details']}$`) &&
                          navButtonStyles.active
                      )}
                    >
                      Configuration
                    </Button>
                  </Link>
                  {isUserOrderAdmin && (
                    <Link href={`${SHOP_ADMIN_PAGES_URLS['order-administration']}?shp_id=${shopId}`}>
                      <Button
                        variant="text"
                        color="secondary"
                        startIcon={<BarsSortSvg className={sharedStyles.icon} />}
                        className={clsx(
                          navButtonStyles.navButton,
                          isRouterPathMatchingRoute(
                            router.pathname,
                            `^${SHOP_ADMIN_PAGES_URLS['order-administration']}$`
                          ) && navButtonStyles.active
                        )}
                      >
                        Commandes
                      </Button>
                    </Link>
                  )}
                  <Link href={`${SHOP_ADMIN_PAGES_URLS['order-statistics']}?shp_id=${shopId}`}>
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<StatsSvg className={sharedStyles.icon} />}
                      className={clsx(
                        navButtonStyles.navButton,
                        isRouterPathMatchingRoute(router.pathname, `^${SHOP_ADMIN_PAGES_URLS['order-statistics']}$`) &&
                          navButtonStyles.active
                      )}
                    >
                      Chiffres
                    </Button>
                  </Link>
                  <Link href={`${SHOP_ADMIN_PAGES_URLS['best-sellers']}?shp_id=${shopId}`}>
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<StatsSvg className={sharedStyles.icon} />}
                      className={clsx(
                        navButtonStyles.navButton,
                        isRouterPathMatchingRoute(router.pathname, `^${SHOP_ADMIN_PAGES_URLS['best-sellers']}$`) &&
                          navButtonStyles.active
                      )}
                    >
                      Meilleures ventes
                    </Button>
                  </Link>
                  <Link href={`${SHOP_ADMIN_PAGES_URLS['order-billing']}?shp_id=${shopId}`}>
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<FileInvoiceDollarSvg className={sharedStyles.icon} />}
                      className={clsx(
                        navButtonStyles.navButton,
                        isRouterPathMatchingRoute(router.pathname, `^${SHOP_ADMIN_PAGES_URLS['order-billing']}$`) &&
                          navButtonStyles.active
                      )}
                    >
                      Facturation
                    </Button>
                  </Link>
                  <Link href={`${SHOP_ADMIN_PAGES_URLS['users']}?shp_id=${shopId}`}>
                    <Button
                      variant="text"
                      color="secondary"
                      startIcon={<TargetAudienceSvg className={sharedStyles.icon} />}
                      className={clsx(
                        navButtonStyles.navButton,
                        isRouterPathMatchingRoute(router.pathname, `^${SHOP_ADMIN_PAGES_URLS['users']}$`) &&
                          navButtonStyles.active
                      )}
                    >
                      Clients
                    </Button>
                  </Link>
                </>
              )}
              {!isShopLoading && shop != null && (
                <Link href={`${shop.websiteUrl}`}>
                  <Button
                    variant="text"
                    color="tertiary"
                    startIcon={<BrowserSvg className={sharedStyles.icon} />}
                    className={navButtonStyles.navButton}
                  >
                    Site Web
                    <Stack component="span" ml={0.5}>
                      <Icon name="expand" size="small" />
                    </Stack>
                  </Button>
                </Link>
              )}
            </>
          )) ||
            null}
          <Stack width="100%" px={2} py={1}>
            <Divider />
          </Stack>
          <UnstyledLink href={`${SHOP_ADMIN_ACCOUNT_PAGES_URLS['home']}`}>
            <Stack direction="row" alignItems="center" my={1} ml={2} spacing={1}>
              <Avatar>{formatAvatarInitials(userName)}</Avatar>
              <Typography
                variant="body2"
                component="div"
                fontWeight="500"
                maxWidth={172}
                textOverflow="ellipsis"
                overflow="hidden"
              >
                {account?.firstName}
              </Typography>
            </Stack>
          </UnstyledLink>
        </div>
      </nav>
    </ClickAwayListener>
  );
};

export default MainNav;
